import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { audienceService } from '../services';
import { useAuth } from '../context/AuthContext';
import { PhoneField } from '../components/auth/AuthField';
import TintSquare from '../components/hand/TintSquare';
import PortalLogo from '../components/PortalLogo';
import { ringBorder } from '../components/portalkit';

const MONO = "'JetBrains Mono', Menlo, monospace";
const RING = 'linear-gradient(90deg,#FD199F,#FF33C6,#B5B1FF,#127BFD,#22ECFB,#FAE1B4,#FCAA6D,#FD199F)';
const EMAIL_OK = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * heyportal.app/join/{code} — where a club's or Pro page's contact link (and its QR) lands.
 * On black, like every Portal link that reaches people who aren't in the app yet.
 *
 * What it asks for is the page's call (WHAT THEY HAVE TO GIVE on the manage page): by default
 * an email or a phone, whichever they prefer, and a name if they like; a page can insist on
 * one of them, or on both. Signed in, their account is the identity — the page only asks for
 * an address the account doesn't carry. Then the invitation to make a Portal, which claims the
 * spot and follows the page on its own.
 *
 * The primary button is the HOST / FILTERS pill: black inside the animated ring, not a rainbow slab.
 */

/** The black pill inside the animated ring — the header's HOST button, as a full-width action. */
function RingButton({ children, onClick, disabled, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="pk-ring-anim h-12 w-full rounded-full font-bold uppercase text-white disabled:opacity-40"
      style={{ fontSize: 12, letterSpacing: '0.14em', border: '2px solid transparent', boxSizing: 'border-box', background: ringBorder('#000'), fontFamily: MONO, cursor: disabled ? 'not-allowed' : 'pointer' }}
    >
      {children}
    </button>
  );
}

export default function ContactLinkPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [info, setInfo] = useState(null);
  const [missing, setMissing] = useState(false);
  const [mode, setMode] = useState('phone'); // phone | email — only when the page takes either
  const [phone, setPhone] = useState({ country: 'CA', number: '' });
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => {
    let live = true;
    audienceService.describeLink(code).then((r) => {
      if (!live) return;
      if (r.success) setInfo(r.data); else setMissing(true);
    });
    return () => { live = false; };
  }, [code, isAuthenticated]);

  const requireEmail = !!info?.requireEmail;
  const requirePhone = !!info?.requirePhone;
  // Which fields this page shows. Signed in, only what the account is missing.
  const askEmail = isAuthenticated ? requireEmail && !info?.viewerHasEmail : (requireEmail || (!requirePhone && mode === 'email'));
  const askPhone = isAuthenticated ? requirePhone && !info?.viewerHasPhone : (requirePhone || (!requireEmail && mode === 'phone'));
  const showChooser = !isAuthenticated && !requireEmail && !requirePhone;
  const phoneOk = phone.number.replace(/\D/g, '').length >= 7;
  const emailOk = EMAIL_OK.test(email.trim());
  const canJoin = (!askEmail || emailOk) && (!askPhone || phoneOk) && (isAuthenticated || askEmail || askPhone);

  const join = async () => {
    setBusy(true); setError(null);
    const payload = {};
    if (askEmail) payload.email = email.trim();
    if (askPhone) { payload.phone = phone.number; payload.phoneCountry = phone.country; }
    if (!isAuthenticated) payload.name = name;
    const r = await audienceService.joinLink(code, payload);
    setBusy(false);
    if (!r.success) { setError(r.error || 'That did not go through. Check what you typed.'); return; }
    setDone(r.data);
  };

  const followWord = info?.ownerType === 'club' ? 'JOIN' : 'FOLLOW';
  const publicPath = info?.publicPath || '/';
  const registerTo = `/register?redirect=${encodeURIComponent(publicPath)}`;
  const loginTo = `/login?redirect=${encodeURIComponent(`/join/${code}`)}`;

  const frame = (children) => (
    <div className="flex min-h-screen flex-col items-center bg-black px-6 text-white" style={{ fontFamily: MONO, paddingTop: 'max(48px, env(safe-area-inset-top))', paddingBottom: 48 }} data-testid="contact-link-page">
      <button type="button" onClick={() => navigate('/featured')} aria-label="Portal" style={{ background: 'none', border: 0, color: '#fff', padding: 0, marginBottom: 40 }}>
        <PortalLogo cap={24} />
      </button>
      <div className="flex w-full flex-col items-center" style={{ maxWidth: 420, gap: 24 }}>{children}</div>
    </div>
  );

  if (missing) {
    return frame(
      <>
        <p className="font-bold uppercase" style={{ fontSize: 14, letterSpacing: '0.14em' }}>No page at that link.</p>
        <p style={{ fontSize: 11, letterSpacing: '0.1em', color: 'rgba(255,255,255,.6)', textAlign: 'center' }}>IT MAY HAVE BEEN REPLACED, OR THE LINK IS MISSING A LETTER.</p>
        <button type="button" onClick={() => navigate('/featured', { replace: true })} className="h-11 rounded-full bg-white px-6 font-bold uppercase text-black" style={{ fontSize: 12, letterSpacing: '0.14em' }}>See what’s on</button>
      </>,
    );
  }
  if (!info) return frame(<p className="font-bold uppercase" style={{ fontSize: 11, letterSpacing: '0.16em', color: 'rgba(255,255,255,.6)' }}>Opening…</p>);

  const header = (
    <div className="flex flex-col items-center text-center" style={{ gap: 12 }}>
      <div style={{ padding: 3, borderRadius: '50%', background: RING }}>
        <div style={{ borderRadius: '50%', overflow: 'hidden', background: '#000', padding: 3 }}>
          <TintSquare name={info.name} imageUrl={info.avatarUrl} size={88} className="!border-0" style={{ borderRadius: '50%' }} />
        </div>
      </div>
      <span className="font-bold uppercase" style={{ fontSize: 9, letterSpacing: '0.2em', color: 'rgba(255,255,255,.55)' }}>{info.ownerType === 'club' ? 'A CLUB ON PORTAL' : 'ON PORTAL'}</span>
      <h1 className="m-0 font-bold uppercase" style={{ fontSize: 24, letterSpacing: '0.06em', lineHeight: 1.15 }}>{info.name}</h1>
      {(info.headline || info.tagline) && <p className="m-0" style={{ fontSize: 13, lineHeight: 1.55, color: 'rgba(255,255,255,.75)', maxWidth: 340 }}>{info.headline || info.tagline}</p>}
    </div>
  );

  if (done) {
    const via = askPhone && !askEmail ? 'number' : 'email';
    const line = {
      following: `You now follow ${info.name}. Their next email or text comes straight to you.`,
      member: `You're in. ${info.name} is on your Portal now — the feed, the events, the lot.`,
      invited: `You're on the list, and ${info.name} has sent you an invitation — the club is paid, so joining is up to you.`,
      already: `You're already on ${info.name}'s list. Nothing to do.`,
      on_list: done.hasAccount
        ? `You're on the list. Sign in and ${info.name} is already on your Portal.`
        : `You're on the list. Make a Portal with the same ${via} and you'll ${info.ownerType === 'club' ? 'be in the club' : 'follow the page'} the moment you do.`,
    }[done.outcome] || `You're on the list.`;
    return frame(
      <>
        {header}
        <div className="w-full text-center" style={{ padding: '22px 20px', borderRadius: 22, background: '#111' }} data-testid="join-done">
          <p className="m-0 font-bold uppercase" style={{ fontSize: 12, letterSpacing: '0.16em' }}>{done.outcome === 'already' ? 'ALREADY ON IT' : done.outcome === 'member' ? 'YOU’RE IN' : done.outcome === 'following' ? 'FOLLOWING' : 'ON THE LIST'}</p>
          <p className="m-0" style={{ fontSize: 13, lineHeight: 1.6, color: 'rgba(255,255,255,.75)', marginTop: 10 }}>{line}</p>
        </div>
        {isAuthenticated || done.hasAccount ? (
          <button type="button" onClick={() => navigate(isAuthenticated ? publicPath : loginTo)} className="h-12 w-full rounded-full bg-white font-bold uppercase text-black" style={{ fontSize: 12, letterSpacing: '0.14em' }}>
            {isAuthenticated ? `OPEN ${info.name.toUpperCase()} →` : 'SIGN IN →'}
          </button>
        ) : (
          <>
            <RingButton onClick={() => navigate(registerTo)} testId="create-portal">CREATE YOUR PORTAL →</RingButton>
            <button type="button" onClick={() => navigate(loginTo)} className="font-bold uppercase" style={{ background: 'none', border: 0, color: 'rgba(255,255,255,.6)', fontSize: 10, letterSpacing: '0.14em' }}>ALREADY HAVE ONE? SIGN IN</button>
          </>
        )}
      </>,
    );
  }

  // One white pill: the country (CA +1 ▾) then the number. No hairline box, no focus ring.
  const phoneField = (
    <div data-testid="join-phone">
      <PhoneField tone="light" label={null} id="join-phone" country={phone.country} number={phone.number} onChange={setPhone} autoFocus={!askEmail} height={48} radius={16} />
    </div>
  );
  const emailField = (
    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@somewhere.com" aria-label="Email" autoFocus
      className="h-12 w-full rounded-2xl px-5 text-black outline-none" style={{ fontFamily: MONO, fontSize: 15, background: '#fff', border: 0 }} data-testid="join-email" />
  );
  const asksLine = requireEmail && requirePhone ? `${info.name} asks for both an email and a phone number.` : requireEmail ? `${info.name} asks for an email address.` : requirePhone ? `${info.name} asks for a phone number.` : null;

  const signedInBody = (
    <>
      <p className="m-0 text-center" style={{ fontSize: 12, lineHeight: 1.6, color: 'rgba(255,255,255,.7)' }}>
        Signed in as <span className="font-bold text-white">{user?.displayName || user?.email}</span>.
        {info.alreadyFollowing
          ? ` You already ${info.ownerType === 'club' ? 'belong to' : 'follow'} ${info.name}.`
          : askEmail || askPhone
            ? ` ${info.name} asks for ${askEmail && askPhone ? 'an email and a phone number' : askEmail ? 'an email address' : 'a phone number'} your account doesn't have yet.`
            : ` One tap puts you on ${info.name}'s list${info.isPaidClub ? '' : info.ownerType === 'club' ? ' and in the club' : ' and follows the page'}.`}
      </p>
      {!info.alreadyFollowing && (askEmail || askPhone) && (
        <div className="flex w-full flex-col" style={{ gap: 10 }} data-testid="join-missing">
          {askEmail && emailField}
          {askPhone && phoneField}
        </div>
      )}
      {error && <p className="m-0" style={{ fontSize: 11, letterSpacing: '0.06em', color: '#FF66C4' }}>{error}</p>}
      {info.alreadyFollowing ? (
        <button type="button" onClick={() => navigate(publicPath)} className="h-12 w-full rounded-full bg-white font-bold uppercase text-black" style={{ fontSize: 12, letterSpacing: '0.14em' }}>OPEN {info.name.toUpperCase()} →</button>
      ) : (
        <RingButton onClick={join} disabled={busy || !canJoin} testId="join-signed-in">{busy ? '…' : `${followWord} ${info.name.toUpperCase()}`}</RingButton>
      )}
    </>
  );

  const signedOutBody = (
    <>
      {showChooser && (
        <div className="flex w-full" style={{ gap: 6 }} role="radiogroup" aria-label="How to reach you">
          {[['phone', 'TEXT ME'], ['email', 'EMAIL ME']].map(([k, label]) => (
            <button key={k} type="button" role="radio" aria-checked={mode === k} onClick={() => setMode(k)}
              className="h-10 flex-1 rounded-full font-bold uppercase transition-colors duration-200"
              style={{ fontSize: 10, letterSpacing: '0.14em', background: mode === k ? '#fff' : '#1A1A1A', color: mode === k ? '#000' : 'rgba(255,255,255,.7)', border: 0 }}>
              {label}
            </button>
          ))}
        </div>
      )}
      {asksLine && <p className="m-0 text-center font-bold uppercase" style={{ fontSize: 10, letterSpacing: '0.14em', color: 'rgba(255,255,255,.6)' }} data-testid="join-asks">{asksLine}</p>}
      <div className="flex w-full flex-col" style={{ gap: 10 }}>
        {askEmail && emailField}
        {askPhone && phoneField}
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} placeholder="Your name (optional)" aria-label="Your name"
          className="h-12 w-full rounded-2xl px-5 text-white outline-none" style={{ fontFamily: MONO, fontSize: 14, background: '#1A1A1A', border: 0 }} />
      </div>
      {error && <p className="m-0" style={{ fontSize: 11, letterSpacing: '0.06em', color: '#FF66C4' }}>{error}</p>}
      <RingButton onClick={join} disabled={!canJoin || busy} testId="join-submit">{busy ? '…' : 'GET ON THE LIST'}</RingButton>
      <p className="m-0 text-center" style={{ fontSize: 10, lineHeight: 1.6, color: 'rgba(255,255,255,.45)' }}>
        {info.name} can email or text you. Every message has a way out. <button type="button" onClick={() => navigate(loginTo)} className="font-bold uppercase" style={{ background: 'none', border: 0, color: 'rgba(255,255,255,.7)', fontSize: 10, letterSpacing: '0.12em', padding: 0 }}>Already on Portal? Sign in</button>
      </p>
    </>
  );

  return frame(
    <>
      {header}
      {isAuthenticated ? signedInBody : signedOutBody}
    </>,
  );
}
